<script lang="ts">
import { type Attachment, AttachmentType } from '@smartsupp/smartsupp-message'
import { onMount } from 'svelte'
import { fade } from 'svelte/transition'

import Carousel from '@/components/carousel/Carousel.svelte'
import Card from '@/components/content/Card.svelte'
import MessageHtml from '@/components/content/MessageHtml.svelte'
import MessageText from '@/components/content/MessageText.svelte'
import IconButton from '@/components/core/IconButton.svelte'
import IconChevronDown from '@/components/icons/IconChevronDown.svelte'
import Linkifier from '@/components/Linkifier.svelte'
import { testIds } from '@/constants'
import { isTouchDevice } from '@/modules/device'
import { popupMessage } from '@/modules/messages'
import { t } from '@/modules/translations'

const MESSAGE_SCROLL_OFFSET = 110

const findCards = (attachments: Attachment[]): Attachment.Cards | null => {
	const cards = attachments.find((a) => a.type === AttachmentType.Cards) as Attachment.Cards
	if (!cards) return null
	return cards
}

let cards = $derived($popupMessage && findCards($popupMessage.attachments))
let singleCard: boolean = $derived(cards ? cards.items.length === 1 : false)
let textDiv: HTMLDivElement | undefined = $state()
let showScrollButton = $state(false)

const checkOverflow = () => {
	if (textDiv) {
		showScrollButton =
			textDiv.scrollHeight > textDiv.clientHeight &&
			textDiv.scrollTop + textDiv.clientHeight < textDiv.scrollHeight - 12
	}
}

const scrollToBottom = (e: MouseEvent) => {
	e.stopPropagation()
	if (textDiv) {
		textDiv.scrollBy({ top: MESSAGE_SCROLL_OFFSET, behavior: 'smooth' })
	}
}

onMount(() => {
	// Check overflow when component is mounted
	checkOverflow()
})

const overflowEventListeners = (element: HTMLDivElement) => {
	element.addEventListener('scroll', checkOverflow)
	window.addEventListener('resize', checkOverflow)

	return {
		destroy() {
			element.removeEventListener('scroll', checkOverflow)
			window.removeEventListener('resize', checkOverflow)
		},
	}
}
</script>

{#if $popupMessage?.content.text}
	<div class="relative">
		<div
			class="text-sm leading-[22px] overflow-auto max-h-132px color-slate-800"
			data-testid={testIds.popupMessage}
			bind:this={textDiv}
			use:overflowEventListeners
		>
			{#if 'html' in $popupMessage.content && $popupMessage.content.html}
				<MessageText>
					<MessageHtml>
						{@html $popupMessage.content.html}
					</MessageHtml>
				</MessageText>
			{:else if $popupMessage.content.text}
				<MessageText>
					<Linkifier text={$popupMessage.content.text} />
				</MessageText>
			{/if}
		</div>
		{#if showScrollButton}
			<div
				class="absolute bottom-0 w-full flex items-center justify-center bg-gradient-to-t from-white/100 from-0% via-white/50 via-70% to-transparent"
				transition:fade={{ duration: 200 }}
			>
				<IconButton
					icon={IconChevronDown}
					iconDescription={$t('ariaLabel.scrollDown')}
					onClick={scrollToBottom}
					variant="primary"
					size="sm"
				/>
			</div>
		{/if}
	</div>
{/if}
{#if cards}
	<!--Needs to be wrapped with `key` block to handle rendering of Carousel when the cards array changes-->
	{#key cards.items.length}
		<div class="w-full flex-center py-2 overflow-clip">
			<div class={['relative w-56', singleCard && 'w-full']}>
				<Carousel controlsTop="4rem" controls={!isTouchDevice()}>
					{#each cards.items as item}
						<Card card={item} isCompact fullWidth={singleCard} />
					{/each}
				</Carousel>
			</div>
		</div>
	{/key}
{/if}
